/**
 * portal/src/lib/telemetry.ts
 *
 * Visit + action telemetry for the portal.
 * Fires-and-forgets — failures are swallowed silently. Should NEVER
 * block any user flow or show errors. Captures only non-PII data:
 * page path, referrer, screen dimensions, timezone. General signup/login
 * telemetry hashes email. Promo applications intentionally submit email and
 * application details to /v1/promo/applications for admin review.
 *
 * Consent: our Privacy Policy discloses anonymous telemetry. No
 * cookies, no fingerprinting, no cross-site tracking.
 */

import { getRememberedAttribution } from './attribution';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.zitotrontech.com';
const ENDPOINT = `${API_BASE}/v1/portal/telemetry`;
const PROMO_ENDPOINT = `${API_BASE}/v1/promo/applications`;
const LEAD_VISITOR_KEY = 'agentomatic_lead_visitor_id';
const LEAD_SESSION_KEY = 'agentomatic_lead_session_id';

/** SHA-256 hash of an email so we can correlate events without storing PII. */
async function hashEmail(email: string): Promise<string> {
  try {
    const enc = new TextEncoder();
    const buf = await crypto.subtle.digest('SHA-256', enc.encode(email.trim().toLowerCase()));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}

function getScreen(): string {
  try { return `${window.screen.width}x${window.screen.height}`; } catch { return ''; }
}
function getTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}
function getPage(): string {
  try { return window.location.pathname; } catch { return ''; }
}
function getReferrer(): string {
  try { return document.referrer || ''; } catch { return ''; }
}

function makeClientId(prefix: string): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `${prefix}_${crypto.randomUUID()}`;
    }
  } catch { /* ignore */ }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function getStoredId(storage: Storage | undefined, key: string, prefix: string): string {
  if (!storage) return makeClientId(prefix);
  try {
    const existing = storage.getItem(key);
    if (existing) return existing;
    const next = makeClientId(prefix);
    storage.setItem(key, next);
    return next;
  } catch {
    return makeClientId(prefix);
  }
}

function getLeadIds(): Record<string, string> {
  return {
    lead_visitor_id: getStoredId(window.localStorage, LEAD_VISITOR_KEY, 'lv'),
    lead_session_id: getStoredId(window.sessionStorage, LEAD_SESSION_KEY, 'ls'),
  };
}

function withAttribution(extra?: Record<string, unknown>): Record<string, unknown> | undefined {
  const attribution = getRememberedAttribution();
  const hasAttribution = Object.keys(attribution).length > 0;
  const leadIds = getLeadIds();
  return hasAttribution ? { ...extra, ...leadIds, attribution } : { ...extra, ...leadIds };
}

export function getLeadTrackingContext(extra?: Record<string, unknown>): Record<string, unknown> {
  return withAttribution(extra) || {};
}

async function fire(payload: Record<string, string | undefined>): Promise<void> {
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // keepalive ensures the request completes even if the page navigates away.
      keepalive: true,
    });
  } catch {
    // Swallow — analytics failures must never surface.
  }
}

export async function trackEvent(action: string, extra?: Record<string, unknown>): Promise<void> {
  const enriched = withAttribution(extra);
  await fire({
    action,
    page: getPage(),
    referrer: getReferrer(),
    screen: getScreen(),
    timezone: getTimezone(),
    extra: enriched ? JSON.stringify(enriched) : undefined,
  });
}

/** Track a page view. Called on route changes. */
export async function trackPageView(path?: string): Promise<void> {
  const enriched = withAttribution();
  await fire({
    action: 'page_view',
    page: path || getPage(),
    referrer: getReferrer(),
    screen: getScreen(),
    timezone: getTimezone(),
    extra: enriched ? JSON.stringify(enriched) : undefined,
  });
}

/** Track a signup event. Email is hashed. */
export async function trackSignup(email: string, tier: string = 'community'): Promise<void> {
  const emailHash = await hashEmail(email);
  const enriched = withAttribution({ tier });
  await fire({
    action: 'signup',
    page: getPage(),
    referrer: getReferrer(),
    screen: getScreen(),
    timezone: getTimezone(),
    emailHash,
    extra: enriched ? JSON.stringify(enriched) : JSON.stringify({ tier }),
  });
}

/** Track and persist a promo application for admin review. */
export async function trackPromoApplication(email: string, application: Record<string, unknown>): Promise<void> {
  const emailHash = await hashEmail(email);
  const enriched = withAttribution({ tier: 'may10-promo', application });
  const attribution = getRememberedAttribution();
  const persisted = fetch(PROMO_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      source: application.source || 'portal',
      page: getPage(),
      referrer: getReferrer(),
      attribution,
      ...application,
    }),
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Promo application failed (${res.status})`);
    }
  });
  const tracked = fire({
    action: 'promo_application',
    page: getPage(),
    referrer: getReferrer(),
    screen: getScreen(),
    timezone: getTimezone(),
    emailHash,
    extra: enriched ? JSON.stringify(enriched) : JSON.stringify({ tier: 'may10-promo', application }),
  });
  await Promise.allSettled([tracked]);
  await persisted;
}

/** Track a login event. Email is hashed. */
export async function trackLogin(email: string): Promise<void> {
  const emailHash = await hashEmail(email);
  await fire({
    action: 'login',
    page: getPage(),
    screen: getScreen(),
    timezone: getTimezone(),
    emailHash,
  });
}

/** Track an upgrade / plan-switch. Email is hashed. */
export async function trackUpgrade(email: string, tier: string, billing: string): Promise<void> {
  const emailHash = await hashEmail(email);
  const enriched = withAttribution({ tier, billing });
  await fire({
    action: 'upgrade',
    page: getPage(),
    screen: getScreen(),
    timezone: getTimezone(),
    emailHash,
    extra: enriched ? JSON.stringify(enriched) : JSON.stringify({ tier, billing }),
  });
}
